import { Icon } from "@iconify/react/dist/iconify.js";
import { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "./ui/LoadingComponent";
import TableDataNoAvailable from "./ui/TableDataNoAvailable";
import PaginationControls from "./ui/PaginationControls";
import PerPageDropdown from "./ui/PerPageDropdown";
import { usePermissions } from "../context/PermissionContext";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import Shimmer from "./ui/Shimmer";
import SortIcons from "./ui/SortIcons";
import ErrorAlert from "./ui/ErrorAlert";

const VehicleTypesLayer = () => {
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [filteredVehicleTypes, setFilteredVehicleTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalType, setModalType] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [vehicleTypeId, setVehicleTypeId] = useState(null);
  const [typeName, setTypeName] = useState("");
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [description, setDescription] = useState("");
  const [typeImage, setTypeImage] = useState(null);
  const [selectedVehicleType, setSelectedVehicleType] = useState(null);
  const [perPage, setPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [saveLoading, setSaveLoading] = useState(false);
  const [vehicleTypeLoading, setVehicleTypeLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    field: "type_name",
    direction: "asc",
  });
  const [expandedRows, setExpandedRows] = useState({});
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [aspectRatio, setAspectRatio] = useState("1:1");
  const [error, setError] = useState(null);

  const cropperRef = useRef(null);
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const fetchVehicleTypes = async (
    page = 1,
    perPage = 10,
    search = "",
    sortBy = "type_name",
    sortOrder = "asc"
  ) => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = {
        page,
        per_page: perPage,
        sort_by: sortBy,
        sort_order: sortOrder,
        want_pagination: 1,
      };
      if (search.trim())
        queryParams.type_name = encodeURIComponent(search.trim());
      const response = await apiCall(
        "get",
        "/get-all-vehicle-types",
        queryParams,
        navigate
      );
      if (response?.data?.success) {
        const { data, pagination } = response.data;
        const vehicleTypeList = data || [];
        const total = pagination?.total || vehicleTypeList.length;
        const currentPageFromResponse = pagination?.current_page || page;
        const lastPage = pagination?.last_page || Math.ceil(total / perPage);

        setVehicleTypes(vehicleTypeList);

        // Apply client-side filtering if search query exists
        if (search.trim()) {
          const filtered = vehicleTypeList.filter((vt) =>
            vt.type_name?.toLowerCase().includes(search.toLowerCase())
          );
          setFilteredVehicleTypes(filtered);
          setTotalEntries(filtered.length);
          setTotalPages(Math.ceil(filtered.length / perPage));
        } else {
          setFilteredVehicleTypes(vehicleTypeList);
          setTotalEntries(total);
          setTotalPages(parseInt(lastPage, 10));
        }

        setCurrentPage(parseInt(currentPageFromResponse, 10));
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "GET");
      } else {
        handleCustomResponse("error", "Failed to load vehicle types.");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "GET");
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleTypeById = async (vehicleTypeId) => {
    try {
      setVehicleTypeLoading(true);
      const response = await apiCall(
        "get",
        `/get-vehicle-type/${vehicleTypeId}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "GET");
        return response.data.data || response.data.vehicle_type || null;
      } else {
        handleCustomResponse("error", "Failed to fetch vehicle type details.");
        return null;
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "GET");
      return null;
    } finally {
      setVehicleTypeLoading(false);
    }
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const debouncedFetchVehicleTypes = useCallback(
    debounce((page, perPage, search) => {
      fetchVehicleTypes(
        page,
        perPage,
        search,
        sortConfig.field,
        sortConfig.direction
      );
    }, 300), // Reduced to 300ms for better responsiveness
    [sortConfig]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
    debouncedFetchVehicleTypes(1, perPage, value);
  };

  const handleSort = (field) => {
    if (field === "created_by") return;
    const newDirection =
      sortConfig.field === field && sortConfig.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({ field, direction: newDirection });
    fetchVehicleTypes(currentPage, perPage, searchQuery, field, newDirection);
  };

  const sortedVehicleTypes = [...filteredVehicleTypes].sort((a, b) => {
    if (!sortConfig.field) return 0;
    const valueA = a[sortConfig.field] || "";
    const valueB = b[sortConfig.field] || "";
    if (sortConfig.field === "is_active") {
      const valA = valueA ? "Active" : "Inactive";
      const valB = valueB ? "Active" : "Inactive";
      return sortConfig.direction === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }
    return sortConfig.direction === "asc"
      ? valueA.toString().localeCompare(valueB.toString())
      : valueB.toString().localeCompare(valueA.toString());
  });

  useEffect(() => {
    fetchVehicleTypes(
      currentPage,
      perPage,
      searchQuery,
      sortConfig.field,
      sortConfig.direction
    );
  }, [currentPage, perPage]);

  // useEffect(() => {
  //   console.log("Filtered Vehicle Types Updated:", filteredVehicleTypes);
  // }, [filteredVehicleTypes]);

  const handleOpenModal = async (type, vehicleType = null) => {
    setModalType(type);
    setShowModal(true);
    setVehicleTypeId(null);
    setTypeName("");
    setDescription("");
    setTypeImage(null);
    setUploadedImageUrl(null);
    setSelectedVehicleType(null);

    if (type === "edit" && vehicleType) {
      if (!vehicleType.type_id || isNaN(vehicleType.type_id)) {
        handleCustomResponse("error", "Invalid or missing Vehicle Type ID.");
        return;
      }
      const vehicleTypeData = await fetchVehicleTypeById(vehicleType.type_id);
      if (vehicleTypeData) {
        setVehicleTypeId(vehicleTypeData.type_id);
        setTypeName(vehicleTypeData.type_name || "");
        setDescription(vehicleTypeData.description || "");
        setTypeImage(null);
        setSelectedVehicleType(vehicleTypeData);
      } else {
        handleCustomResponse("error", "Failed to load vehicle type details.");
      }
    } else if (type === "delete" && vehicleType) {
      if (!vehicleType.type_id || isNaN(vehicleType.type_id)) {
        handleCustomResponse("error", "Invalid or missing Vehicle Type ID.");
        return;
      }
      setSelectedVehicleType(vehicleType);
      setVehicleTypeId(vehicleType.type_id);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalType("");
    setVehicleTypeId(null);
    setTypeName("");
    setDescription("");
    setTypeImage(null);
    setUploadedImageUrl(null);
    setImageUploading(false);
    setSelectedVehicleType(null);
  };

  const handleOpenImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        handleCustomResponse("error", "Image size should not exceed 5MB.");
        return;
      }
      if (!file.type.startsWith("image/")) {
        handleCustomResponse("error", "Please select a valid image file.");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setImageToCrop(reader.result);
        setShowCropModal(true);
        setAspectRatio("1:1");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCrop = async () => {
    if (cropperRef.current) {
      const canvas = cropperRef.current.cropper.getCroppedCanvas({
        width: 200,
        height: 200,
      });

      canvas.toBlob(
        async (blob) => {
          if (!blob) {
            handleCustomResponse("error", "Failed to crop image.");
            return;
          }
          const file = new File([blob], `vehicle-type-${Date.now()}.png`, {
            type: "image/png",
          });
          setTypeImage(file);
          setShowCropModal(false);
          await handleImageUpload(file);
        },
        "image/png",
        0.8
      );
    } else {
      handleCustomResponse("error", "Cropper not initialized.");
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) {
      handleCustomResponse("error", "No image selected for upload.");
      return;
    }

    try {
      setImageUploading(true);
      const formData = new FormData();
      formData.append("vehicle_type", file);

      const response = await apiCall(
        "post",
        "/upload-image",
        formData,
        navigate,
        true
      );

      if (response?.data?.status === "success" && response?.data?.tiny_url) {
        setUploadedImageUrl(response.data.tiny_url);
        handleCustomResponse("success", "Image uploaded successfully!");
      } else {
        handleCustomResponse("error", "Failed to upload image.");
        setTypeImage(null);
      }
    } catch (error) {
      handleCustomResponse("error", "Error uploading image.");
      setTypeImage(null);
    } finally {
      setImageUploading(false);
    }
  };

  const handleCloseCropModal = () => {
    setShowCropModal(false);
    setImageToCrop(null);
    setAspectRatio("1:1");
  };

  const handleCropMove = () => {
    if (cropperRef.current) {
      const cropBoxData = cropperRef.current.cropper.getCropBoxData();
      const width = cropBoxData.width;
      const height = cropBoxData.height;
      const ratio = width / height;
      setAspectRatio(`${(width / height).toFixed(2)}:1`);
    }
  };

  const handleAddVehicleType = async () => {
    if (!typeName.trim()) {
      handleCustomResponse("error", "Vehicle Type Name is required.");
      return;
    }

    try {
      setSaveLoading(true);

      const formData = new FormData();
      formData.append("type_name", typeName);
      if (description) formData.append("description", description);
      if (uploadedImageUrl) formData.append("type_image", uploadedImageUrl);

      const response = await apiCall(
        "post",
        "/add-vehicle-type",
        formData,
        navigate,
        true
      );

      if (response?.data?.success) {
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "POST");

        setTypeName("");
        setDescription("");
        setTypeImage(null);
        setUploadedImageUrl(null);
        await fetchVehicleTypes(currentPage, perPage, searchQuery);
        handleCloseModal();
      } else {
        handleCustomResponse("error", "Failed to add vehicle type.");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "POST");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleUpdateVehicleType = async () => {
    if (!typeName.trim()) {
      handleCustomResponse("error", "Vehicle Type Name is required.");
      return;
    }

    if (!vehicleTypeId || isNaN(vehicleTypeId)) {
      handleCustomResponse("error", "Invalid or missing Vehicle Type ID.");
      return;
    }

    try {
      setSaveLoading(true);

      const formData = new FormData();
      formData.append("type_name", typeName);
      if (description) formData.append("description", description);
      if (uploadedImageUrl) formData.append("type_image", uploadedImageUrl);

      const response = await apiCall(
        "post",
        `/update-vehicle-type/${vehicleTypeId}`,
        formData,
        navigate,
        true
      );

      if (response?.data?.success) {
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "POST");
        setTypeName("");
        setDescription("");
        setTypeImage(null);
        setUploadedImageUrl(null);
        setVehicleTypeId(null);
        await fetchVehicleTypes(currentPage, perPage, searchQuery);
        handleCloseModal();
      } else {
        handleCustomResponse("error", "Failed to update vehicle type.");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "POST");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteVehicleType = async () => {
    if (!vehicleTypeId || isNaN(vehicleTypeId)) {
      handleCustomResponse("error", "Invalid or missing Vehicle Type ID.");
      return;
    }

    try {
      setSaveLoading(true);
      const response = await apiCall(
        "delete",
        `/delete-vehicle-type/${vehicleTypeId}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "DELETE");
        await fetchVehicleTypes(currentPage, perPage, searchQuery);
        handleCloseModal();
      } else {
        handleCustomResponse("error", "Failed to delete vehicle type.");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "DELETE");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSubmit = () => {
    if (modalType === "add") {
      handleAddVehicleType();
    } else if (modalType === "edit") {
      handleUpdateVehicleType();
    } else if (modalType === "delete") {
      handleDeleteVehicleType();
    }
  };

  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (endPage - startPage < maxPagesToShow - 1) {
      if (startPage === 1) {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      } else if (endPage === totalPages) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const toggleExpand = (typeId, field) => {
    setExpandedRows((prev) => ({
      ...prev,
      [`${typeId}_${field}`]: !prev[`${typeId}_${field}`],
    }));
  };

  const startEntry = totalEntries > 0 ? (currentPage - 1) * perPage + 1 : 0;
  const endEntry = Math.min(currentPage * perPage, totalEntries);

  if (error) {
    return <ErrorAlert />;
  }
  return (
    <div className="p-4 p-md-3 p-sm-2">
      <style>
        {`
          .table-responsive .table td, .table-responsive .table th {
            max-width: 200px;
            vertical-align: middle;
          }
          .show-more-btn {
            color: #E63C23;
            cursor: pointer;
            font-size: 12px;
            margin-left: 5px;
            background: none;
            border: none;
            padding: 0;
          }
          .show-more-btn:hover {
            text-decoration: underline;
          }
          .image-preview {
            cursor: pointer;
            transition: transform 0.2s;
          }
          .image-preview:hover {
            transform: scale(1.05);
          }
          @media (max-width: 576px) {
            .table-responsive .table td, .table-responsive .table th {
              max-width: 100px;
            }
            .show-more-btn {
              font-size: 10px;
            }
          }
          .cropper-container {
            max-height: 400px;
            max-width: 100%;
          }
          .aspect-ratio-display {
            font-size: 14px;
            color: #333;
            margin-top: 10px;
            text-align: center;
          }
        `}
      </style>
      <div className="p-4 p-md-3 p-sm-2">
        <div className="card shadow-sm">
          <div className="card p-3 p-sm-2 p-md-3">
            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
              <div
                className="icon-field flex-grow-1"
                style={{ maxWidth: "400px" }}
              >
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search by vehicle type..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                <span className="icon">
                  <Icon icon="ion:search-outline" />
                </span>
              </div>
              <PerPageDropdown
                perPage={perPage}
                handlePerPageChange={(value) => {
                  setPerPage(value);
                  setCurrentPage(1);
                  fetchVehicleTypes(
                    1,
                    value,
                    searchQuery,
                    sortConfig.field,
                    sortConfig.direction
                  );
                }}
              />
              <div className="ms-auto">
                {hasPermission("insert_vehicle_types") && (
                  <button
                    className="btn btn-primary-600 btn-sm d-flex align-items-center"
                    onClick={() => handleOpenModal("add")}
                  >
                    <Icon icon="ri:add-line" className="me-1" /> Add Vehicle
                    Type
                  </button>
                )}
              </div>
            </div>

            {sortedVehicleTypes.length === 0 && !loading ? (
              <TableDataNoAvailable message="No vehicle types found." />
            ) : (
              <div className="position-relative">
                <div
                  className="table-responsive"
                  style={{
                    opacity: loading ? 0.4 : 1,
                    pointerEvents: loading ? "none" : "auto",
                  }}
                >
                  <table className="table bordered-table mb-0 table-hover">
                    <thead>
                      <tr
                        style={{ backgroundColor: "#475EAB", color: "white" }}
                      >
                        <th scope="col" className="p-2">
                          <span>S.No</span>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("type_name")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Vehicle Type</span>
                            <SortIcons
                              field="type_name"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("created_datetime")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Created Date</span>
                            <SortIcons
                              field="created_datetime"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("type_image")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Image</span>
                            <SortIcons
                              field="type_image"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("is_active")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex ">
                            <span>Status</span>
                            <SortIcons
                              field="is_active"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th scope="col" className="p-2">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedVehicleTypes.map((vehicleType, index) => (
                        <tr key={vehicleType.type_id || index}>
                          <td className="p-2">
                            {(currentPage - 1) * perPage + index + 1}
                          </td>
                          <td className="p-2">
                            <span className="">
                              {expandedRows[`${vehicleType.type_id}_type_name`]
                                ? vehicleType.type_name || "-"
                                : truncateText(vehicleType.type_name || "N-")}
                              {vehicleType.type_name &&
                                vehicleType.type_name.length > 50 && (
                                  <button
                                    className="show-more-btn"
                                    onClick={() =>
                                      toggleExpand(
                                        vehicleType.type_id,
                                        "type_name"
                                      )
                                    }
                                  >
                                    {expandedRows[
                                      `${vehicleType.type_id}_type_name`
                                    ]
                                      ? "Show Less"
                                      : "Show More"}
                                  </button>
                                )}
                            </span>
                          </td>
                          <td className="p-2">
                            {new Date(
                              vehicleType.created_datetime
                            ).toLocaleString() || "-"}
                          </td>
                          <td className="p-2">
                            {vehicleType.type_image ? (
                              <img
                                src={vehicleType.type_image}
                                alt={vehicleType.type_name}
                                className="image-preview"
                                style={{ maxWidth: "50px", maxHeight: "50px" }}
                                onClick={() =>
                                  handleOpenImageModal(vehicleType.type_image)
                                }
                              />
                            ) : (
                              "-"
                            )}
                          </td>
                          <td className="p-2">
                            <span
                              className={`px-3 py-1 radius-4 fw-medium text-sm ${
                                vehicleType.is_active
                                  ? "bg-success-focus text-success-600 border border-success-main"
                                  : "bg-danger-focus text-danger-600 border border-danger-main"
                              }`}
                            >
                              {vehicleType.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="p-2">
                            {hasPermission("update_vehicle_types") && (
                              <button
                                className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                style={{
                                  position: "relative",
                                  transition: "all 0.3s ease",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform =
                                    "scale(1.1)";
                                  const tooltip = document.createElement("div");
                                  tooltip.textContent = "Edit";
                                  tooltip.style.cssText = `
                                position: absolute;
                                top: -35px;
                                left: 50%;
                                transform: translateX(-50%);
                                background-color: #333;
                                color: white;
                                padding: 5px 8px;
                                border-radius: 4px;
                                font-size: 12px;
                                white-space: nowrap;
                                z-index: 1000;
                                pointer-events: none;
                              `;
                                  tooltip.id = "edit-tooltip";
                                  e.currentTarget.appendChild(tooltip);
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = "scale(1)";
                                  const tooltip =
                                    document.getElementById("edit-tooltip");
                                  if (tooltip) tooltip.remove();
                                }}
                                onClick={() =>
                                  handleOpenModal("edit", vehicleType)
                                }
                              >
                                <Icon icon="lucide:edit" />
                              </button>
                            )}
                            {hasPermission("delete_vehicle_types") && (
                              <button
                                className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                style={{
                                  position: "relative",
                                  transition: "all 0.3s ease",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform =
                                    "scale(1.1)";
                                  const tooltip = document.createElement("div");
                                  tooltip.textContent = "Delete";
                                  tooltip.style.cssText = `
                                position: absolute;
                                top: -35px;
                                left: 50%;
                                transform: translateX(-50%);
                                background-color: #333;
                                color: white;
                                padding: 5px 8px;
                                border-radius: 4px;
                                font-size: 12px;
                                white-space: nowrap;
                                z-index: 1000;
                                pointer-events: none;
                              `;
                                  tooltip.id = "delete-tooltip";
                                  e.currentTarget.appendChild(tooltip);
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = "scale(1)";
                                  const tooltip =
                                    document.getElementById("delete-tooltip");
                                  if (tooltip) tooltip.remove();
                                }}
                                onClick={() =>
                                  handleOpenModal("delete", vehicleType)
                                }
                              >
                                <Icon icon="mingcute:delete-2-line" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {loading && (
                  <div
                    className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
                    style={{
                      zIndex: 10,
                      borderRadius: "10px",
                      padding: "20px",
                    }}
                  >
                    <LoadingComponent />
                  </div>
                )}
                <PaginationControls
                  pagination={{
                    currentPage,
                    perPage,
                    total: totalEntries,
                  }}
                  handlePageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className={`modal fade ${showModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showModal ? "rgba(0,0,0,0.5)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {modalType === "add"
                  ? "Add New Vehicle Type"
                  : modalType === "edit"
                  ? "Edit Vehicle Type"
                  : "Delete Vehicle Type"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
              ></button>
            </div>
            <div className="modal-body">
              {modalType === "delete" ? (
                <p>
                  Are you sure you want to delete{" "}
                  <strong>
                    {selectedVehicleType?.type_name || "this vehicle type"}
                  </strong>
                  ?
                </p>
              ) : (
                <>
                  {vehicleTypeLoading ? (
                    <Shimmer type="modal" /> // Show shimmer effect while loading
                  ) : (
                    <>
                      <div className="mb-3">
                        <label htmlFor="typeName" className="form-label">
                          Vehicle Type Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="typeName"
                          value={typeName}
                          onChange={(e) => setTypeName(e.target.value)}
                          placeholder="Enter vehicle type name"
                        />
                      </div>

                      <div className="mb-3">
                        <label htmlFor="typeImage" className="form-label">
                          Vehicle Type Image
                        </label>

                        {/* Hidden actual file input */}
                        <input
                          type="file"
                          className="form-control d-none"
                          id="typeImage"
                          accept="image/*"
                          onChange={handleImageSelect}
                          disabled={imageUploading}
                          ref={(input) => {
                            window.fileInput = input;
                          }}
                        />

                        {/* Custom styled "file input" */}
                        <div
                          className="form-control d-flex align-items-center justify-content-between"
                          style={{ cursor: "pointer" }}
                          onClick={() => window.fileInput?.click()}
                        >
                          <span
                            className={`${
                              uploadedImageUrl ||
                              selectedVehicleType?.type_image
                                ? "text-primary"
                                : "text-muted"
                            }`}
                          >
                            {typeImage
                              ? typeImage.name
                              : selectedVehicleType?.type_image
                              ? selectedVehicleType.type_image.split("/").pop()
                              : "No file chosen"}
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            style={{ pointerEvents: "none" }}
                          >
                            Choose File
                          </button>
                        </div>

                        {imageUploading && (
                          <div className="mt-2">
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Uploading...
                              </span>
                            </div>
                            <span className="ms-2">Uploading image...</span>
                          </div>
                        )}

                        {typeImage && (
                          <div className="mt-2">
                            <img
                              src={URL.createObjectURL(typeImage)}
                              alt="Preview"
                              className="image-preview"
                              style={{ maxWidth: "100px", maxHeight: "100px" }}
                              onClick={() =>
                                handleOpenImageModal(
                                  URL.createObjectURL(typeImage)
                                )
                              }
                            />
                          </div>
                        )}

                        {selectedVehicleType?.type_image && !typeImage && (
                          <div className="mt-2">
                            <img
                              src={selectedVehicleType.type_image}
                              alt={selectedVehicleType.type_name}
                              className="image-preview"
                              style={{ maxWidth: "100px", maxHeight: "100px" }}
                              onClick={() =>
                                handleOpenImageModal(
                                  selectedVehicleType.type_image
                                )
                              }
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn ${
                  modalType === "delete" ? "btn-danger" : "btn-primary-600"
                }`}
                onClick={handleSubmit}
                disabled={saveLoading || vehicleTypeLoading} // Disable button while loading
              >
                {modalType === "add"
                  ? saveLoading
                    ? "Please wait..."
                    : "Add Vehicle Type"
                  : modalType === "edit"
                  ? saveLoading
                    ? "Please wait..."
                    : "Update Vehicle Type"
                  : saveLoading
                  ? "Please wait..."
                  : "Delete Vehicle Type"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal fade ${showCropModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showCropModal ? "rgba(0,0,0,0.5)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Crop Image</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseCropModal}
              ></button>
            </div>
            <div className="modal-body">
              {imageToCrop && (
                <>
                  <Cropper
                    src={imageToCrop}
                    style={{ height: 400, width: "100%" }}
                    initialAspectRatio={1}
                    aspectRatio={1}
                    guides={true}
                    viewMode={1}
                    minCropBoxWidth={200}
                    minCropBoxHeight={200}
                    background={false}
                    responsive={true}
                    autoCropArea={1}
                    checkOrientation={false}
                    crop={handleCropMove}
                    ref={cropperRef}
                  />
                  <p className="aspect-ratio-display">
                    Current Aspect Ratio: {aspectRatio}
                  </p>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseCropModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary-600"
                onClick={handleCrop}
              >
                Crop & Upload
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal fade ${showImageModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showImageModal ? "rgba(0,0,0,0.8)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Image Preview</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseImageModal}
              ></button>
            </div>
            <div className="modal-body d-flex justify-content-center align-items-center">
              {selectedImage && (
                <img
                  src={selectedImage}
                  alt="Vehicle Type"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleTypesLayer;
